import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Calendar as CalendarIcon, Clock, User, Phone, Mail, MapPin, ChevronLeft, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';
import AppointmentModal from '@/components/AppointmentModal';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Calendar = () => {
  const navigate = useNavigate();
  const { getAuthHeader } = useAuth();
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [view, setView] = useState('month'); // month, week, day
  const [showBookingModal, setShowBookingModal] = useState(false);

  useEffect(() => {
    fetchAppointments();
  }, [currentDate]);

  const fetchAppointments = async () => {
    try {
      const response = await axios.get(`${API}/appointments`, {
        headers: getAuthHeader(),
        params: {
          start_date: getStartOfMonth(currentDate).toISOString(),
          end_date: getEndOfMonth(currentDate).toISOString()
        }
      });
      setAppointments(response.data || []);
    } catch (error) {
      toast.error('Failed to fetch appointments');
      setAppointments([]);
    } finally {
      setLoading(false);
    }
  };

  const getStartOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth(), 1);
  };

  const getEndOfMonth = (date) => {
    return new Date(date.getFullYear(), date.getMonth() + 1, 0, 23, 59, 59);
  };

  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDayOfWeek = firstDay.getDay();

    const days = [];
    
    // Previous month days
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = startingDayOfWeek - 1; i >= 0; i--) {
      days.push({
        date: prevMonthLastDay - i,
        isCurrentMonth: false,
        fullDate: new Date(year, month - 1, prevMonthLastDay - i)
      });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({
        date: i,
        isCurrentMonth: true,
        fullDate: new Date(year, month, i)
      });
    }

    // Next month days
    const remainingDays = 42 - days.length; // 6 weeks * 7 days
    for (let i = 1; i <= remainingDays; i++) {
      days.push({
        date: i,
        isCurrentMonth: false,
        fullDate: new Date(year, month + 1, i)
      });
    }

    return days;
  };

  const getAppointmentsForDate = (date) => {
    return appointments.filter(apt => {
      const aptDate = new Date(apt.scheduled_time);
      return aptDate.toDateString() === date.toDateString();
    });
  };

  const isToday = (date) => {
    const today = new Date();
    return date.toDateString() === today.toDateString();
  };

  const isSelected = (date) => {
    return date.toDateString() === selectedDate.toDateString();
  };

  const prevMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  };

  const nextMonth = () => {
    setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));
  };

  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  const days = getDaysInMonth(currentDate);
  const selectedDayAppointments = getAppointmentsForDate(selectedDate);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-gray-700 dark:text-gray-300 font-medium">Loading calendar...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">Calendar</h1>
          <p className="text-gray-600 dark:text-gray-400">Manage appointments and schedule meetings</p>
        </div>
        <Button
          onClick={() => setShowBookingModal(true)}
          className="bg-blue-600 hover:bg-blue-700 text-white"
        >
          <CalendarIcon className="w-4 h-4 mr-2" />
          Book Appointment
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Calendar View */}
        <Card className="lg:col-span-2 p-6 bg-white dark:bg-gray-800 border-0 shadow-lg">
          {/* Calendar Header */}
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
              {monthNames[currentDate.getMonth()]} {currentDate.getFullYear()}
            </h2>
            <div className="flex items-center gap-2">
              <Button
                onClick={prevMonth}
                variant="outline"
                size="sm"
                className="p-2"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              <Button
                onClick={() => setCurrentDate(new Date())}
                variant="outline"
                size="sm"
              >
                Today
              </Button>
              <Button
                onClick={nextMonth}
                variant="outline"
                size="sm"
                className="p-2"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>

          {/* Day Headers */}
          <div className="grid grid-cols-7 gap-2 mb-2">
            {dayNames.map(day => (
              <div key={day} className="text-center text-sm font-semibold text-gray-700 dark:text-gray-300 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Grid */}
          <div className="grid grid-cols-7 gap-2">
            {days.map((day, index) => {
              const dayAppointments = getAppointmentsForDate(day.fullDate);
              const hasAppointments = dayAppointments.length > 0;

              return (
                <button
                  key={index}
                  onClick={() => setSelectedDate(day.fullDate)}
                  className={`
                    aspect-square p-2 rounded-lg transition-all relative
                    ${day.isCurrentMonth ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-600'}
                    ${isToday(day.fullDate) ? 'bg-blue-100 dark:bg-blue-900/50 font-bold text-blue-600 dark:text-blue-300' : ''}
                    ${isSelected(day.fullDate) ? 'ring-2 ring-blue-500 dark:ring-blue-400' : ''}
                    ${hasAppointments ? 'font-semibold' : ''}
                    hover:bg-gray-100 dark:hover:bg-gray-700
                  `}
                >
                  <div className="text-sm">{day.date}</div>
                  {hasAppointments && (
                    <div className="absolute bottom-1 left-1/2 transform -translate-x-1/2 flex gap-1">
                      {dayAppointments.slice(0, 3).map((_, i) => (
                        <div key={i} className="w-1 h-1 rounded-full bg-blue-500 dark:bg-blue-400"></div>
                      ))}
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </Card>

        {/* Appointments List for Selected Date */}
        <Card className="p-6 bg-white dark:bg-gray-800 border-0 shadow-lg">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white mb-4">
            {selectedDate.toLocaleDateString('en-US', { 
              weekday: 'long', 
              month: 'short', 
              day: 'numeric' 
            })}
          </h3>

          {selectedDayAppointments.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <CalendarIcon className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p>No appointments scheduled</p>
            </div>
          ) : (
            <div className="space-y-3">
              {selectedDayAppointments.map((apt, index) => {
                const aptDate = new Date(apt.scheduled_time);
                return (
                  <Card
                    key={index}
                    className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border-blue-200 dark:border-blue-700 hover:shadow-md transition-all cursor-pointer"
                    onClick={() => navigate(`/dashboard/leads?lead_id=${apt.lead_id}`)}
                  >
                    <div className="flex items-start gap-3">
                      <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-600 dark:bg-blue-500 flex items-center justify-center">
                        <Clock className="w-5 h-5 text-white" />
                      </div>
                      <div className="flex-1">
                        <div className="font-semibold text-gray-900 dark:text-gray-100 mb-1">
                          {aptDate.toLocaleTimeString('en-US', { 
                            hour: 'numeric', 
                            minute: '2-digit' 
                          })}
                        </div>
                        {apt.lead_name && (
                          <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 mb-1">
                            <User className="w-3 h-3" />
                            {apt.lead_name}
                          </div>
                        )}
                        {apt.lead_phone && (
                          <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 mb-1">
                            <Phone className="w-3 h-3" />
                            {apt.lead_phone}
                          </div>
                        )}
                        {apt.lead_email && (
                          <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300 mb-1">
                            <Mail className="w-3 h-3" />
                            {apt.lead_email}
                          </div>
                        )}
                        {apt.location && (
                          <div className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                            <MapPin className="w-3 h-3" />
                            {apt.location}
                          </div>
                        )}
                        {apt.notes && (
                          <div className="mt-2 text-sm text-gray-600 dark:text-gray-400 italic">
                            {apt.notes}
                          </div>
                        )}
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>
          )}
        </Card>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 border-0 shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">Total Appointments</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">{appointments.length}</p>
            </div>
            <CalendarIcon className="w-8 h-8 text-green-600 dark:text-green-400" />
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-blue-50 to-cyan-50 dark:from-blue-900/20 dark:to-cyan-900/20 border-0 shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">This Week</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {appointments.filter(apt => {
                  const aptDate = new Date(apt.scheduled_time);
                  const weekStart = new Date();
                  weekStart.setDate(weekStart.getDate() - weekStart.getDay());
                  const weekEnd = new Date(weekStart);
                  weekEnd.setDate(weekEnd.getDate() + 7);
                  return aptDate >= weekStart && aptDate < weekEnd;
                }).length}
              </p>
            </div>
            <Clock className="w-8 h-8 text-blue-600 dark:text-blue-400" />
          </div>
        </Card>

        <Card className="p-4 bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 border-0 shadow">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-700 dark:text-gray-300 mb-1">Today</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {getAppointmentsForDate(new Date()).length}
              </p>
            </div>
            <User className="w-8 h-8 text-purple-600 dark:text-purple-400" />
          </div>
        </Card>
      </div>

      {/* Appointment Booking Modal */}
      <AppointmentModal
        isOpen={showBookingModal}
        onClose={() => setShowBookingModal(false)}
        lead={null}
        getAuthHeader={getAuthHeader}
        onSuccess={fetchAppointments}
      />
    </div>
  );
};

export default Calendar;
